<template>
  <section
    v-if="isVisible"
    class="chat-assistant"
    :class="{ 'chat-assistant-footer-hidden': isFooterHidden }"
    aria-live="polite"
  >
    <transition name="assistant-panel">
      <div
        v-show="isOpen"
        class="chat-window"
        role="dialog"
        aria-labelledby="chat-title"
      >
        <header class="chat-header">
          <div class="header-brand">
            <img :src="logo" alt="EuRoC logo" class="euroc-logo" />
            <div class="header-title">
              <strong id="chat-title">EuRoC Assistant</strong>
              <span>Requirements documents</span>
            </div>
          </div>
          <button
            class="close-btn"
            type="button"
            @click="closeChat"
            aria-label="Close assistant"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <main ref="messagesPane" class="messages">
          <article
            v-for="message in messages"
            :key="message.id"
            class="message"
            :class="message.sender"
          >
            {{ message.text }}
          </article>

          <div v-if="isLoading" class="typing">
            <div class="typing-loader" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <span>Searching requirements documents...</span>
          </div>
        </main>

        <section class="quick-actions" aria-label="Quick questions">
          <button
            v-for="action in quickActions"
            :key="action.label"
            type="button"
            @click="quickAsk(action.question)"
            :disabled="isLoading"
          >
            {{ action.label }}
          </button>
        </section>

        <form class="input-area" @submit.prevent="sendMessage()">
          <input
            ref="chatInput"
            v-model="draft"
            type="text"
            placeholder="Ask a question..."
            autocomplete="off"
            :disabled="isLoading"
            aria-label="Ask the EuRoC assistant"
          />
          <button type="submit" :disabled="!canSend">
            <i class="fa-solid fa-paper-plane"></i>
            Send
          </button>
        </form>
      </div>
    </transition>

    <button
      class="chat-launcher"
      type="button"
      @click="toggleChat"
      :aria-expanded="isOpen.toString()"
      aria-controls="chat-title"
      aria-label="Open EuRoC assistant"
    >
      <span v-if="!isOpen" class="chat-tooltip"
        >Hi, I'm your virtual assistant and I'm happy to assist you.</span
      >
      <i v-if="!isOpen" class="fa-solid fa-comments chat-launcher-icon"></i>
      <i v-else class="fa-solid fa-chevron-down"></i>
    </button>
  </section>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import logo from "@/assets/euroc-logo-new.png";
import {
  GET_EDITION_GETTER,
  GET_USER_LEVEL_GETTER,
  GET_USER_TOKEN_GETTER,
  IS_USER_AUTHENTICATED_GETTER,
} from "../store/storeconstants";

const CHATBOT_ENDPOINT = "https://eurocscoringapi.duartecota.pt/chatbot/chat";

export default {
  name: "ChatAssistant",
  data() {
    return {
      logo,
      isOpen: false,
      isFooterHidden: false,
      lastY: 0,
      isLoading: false,
      draft: "",
      messages: [
        {
          id: 1,
          sender: "bot",
          text: "Hello. I can answer questions about EuRoC requirements, safety rules, mandatory documents, propulsion constraints, and related technical details.",
        },
      ],
      quickActions: [
        {
          label: "Eligibility",
          question: "What are the eligibility requirements?",
        },
        {
          label: "Propulsion",
          question: "What are the propulsion rules?",
        },
        {
          label: "Documents",
          question: "Which documents are mandatory?",
        },
      ],
    };
  },
  computed: {
    ...mapGetters("auth", {
      isAuthenticated: IS_USER_AUTHENTICATED_GETTER,
      token: GET_USER_TOKEN_GETTER,
      edition: GET_EDITION_GETTER,
      userLevel: GET_USER_LEVEL_GETTER,
    }),
    isVisible() {
      const hiddenRoutes = ["/login", "/register", "/resetpassword"];
      return (
        this.isAuthenticated &&
        !hiddenRoutes.includes(String(this.$route.path || "").toLowerCase())
      );
    },
    canSend() {
      return this.draft.trim().length > 0 && !this.isLoading;
    },
    scrollEffectsDisabled() {
      return ["/home", "/main"].includes(
        String(this.$route.path || "").toLowerCase(),
      );
    },
  },
  watch: {
    isOpen(value) {
      if (value) {
        this.isFooterHidden = false;
        this.$nextTick(() => {
          this.scrollToBottom();
          this.$refs.chatInput?.focus();
        });
      }
    },
    messages() {
      this.$nextTick(this.scrollToBottom);
    },
    "$route.path"() {
      this.isFooterHidden = false;
      this.lastY = window.pageYOffset || 0;
    },
  },
  mounted() {
    this.lastY = window.pageYOffset || 0;
    window.addEventListener("scroll", this.onScroll, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.onScroll);
  },
  methods: {
    onScroll() {
      const y = window.pageYOffset || document.documentElement.scrollTop || 0;

      if (this.scrollEffectsDisabled) {
        this.isFooterHidden = false;
        this.lastY = y;
        return;
      }

      if (this.isOpen || y < 80) {
        this.isFooterHidden = false;
        this.lastY = y;
        return;
      }

      if (y - this.lastY > 10) {
        this.isFooterHidden = true;
      } else if (this.lastY - y > 10) {
        this.isFooterHidden = false;
      }

      this.lastY = y;
    },
    toggleChat() {
      this.isOpen = !this.isOpen;
    },
    closeChat() {
      this.isOpen = false;
    },
    quickAsk(question) {
      if (this.isLoading) return;
      this.draft = question;
      this.sendMessage();
    },
    addMessage(text, sender) {
      this.messages.push({
        id: Date.now() + Math.random(),
        sender,
        text,
      });
    },
    async sendMessage() {
      const question = this.draft.trim();
      if (!question || this.isLoading) return;

      this.addMessage(question, "user");
      this.draft = "";
      this.isLoading = true;

      try {
        const response = await axios.post(
          CHATBOT_ENDPOINT,
          {
            message: question,
            context: {
              edition: this.edition,
              route: this.$route.path,
              userLevel: this.userLevel,
            },
          },
          {
            headers: {
              Authorization: this.token,
            },
          },
        );

        this.addMessage(
          response.data?.answer ||
            "No answer found in the requirements documents.",
          "bot",
        );
      } catch (error) {
        this.addMessage(
          "The assistant could not answer right now. Please try again in a moment.",
          "bot",
        );
      } finally {
        this.isLoading = false;
      }
    },
    scrollToBottom() {
      const pane = this.$refs.messagesPane;
      if (pane) pane.scrollTop = pane.scrollHeight;
    },
  },
};
</script>

<style scoped>
.chat-assistant {
  --assistant-yellow: #e4ff26;
  --assistant-blue: #3152f5;
  --assistant-border: rgba(49, 82, 245, 0.16);
  position: fixed;
  right: 16px;
  bottom: 96px;
  z-index: 9999;
  color: #0f172a;
  transition: bottom 0.25s ease;
  will-change: bottom;
}

.chat-assistant-footer-hidden {
  bottom: 16px;
}

.chat-launcher {
  position: relative;
  width: 68px;
  height: 68px;
  border: 0;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #000;
  font-size: 1.45rem;
  background: var(--assistant-yellow);
  box-shadow: 0 18px 36px rgba(49, 82, 245, 0.2),
    0 0 0 8px rgba(228, 255, 38, 0.18);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.chat-launcher-icon {
  color: #0f172a;
  font-size: 1.95rem;
  line-height: 1;
  filter: drop-shadow(0 6px 10px rgba(15, 23, 42, 0.16));
}

.chat-tooltip {
  position: absolute;
  right: calc(100% + 0.85rem);
  bottom: 50%;
  width: max-content;
  max-width: 260px;
  border: 1px solid rgba(49, 82, 245, 0.14);
  border-radius: 14px;
  padding: 0.65rem 0.85rem;
  color: #0f172a;
  font-size: 0.84rem;
  font-weight: 800;
  line-height: 1.3;
  text-align: left;
  pointer-events: none;
  background: #ffffff;
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.16);
  opacity: 0;
  transform: translateY(50%) translateX(8px) scale(0.98);
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.chat-tooltip::after {
  position: absolute;
  right: -7px;
  bottom: 50%;
  width: 14px;
  height: 14px;
  content: "";
  background: #ffffff;
  border-top: 1px solid rgba(49, 82, 245, 0.14);
  border-right: 1px solid rgba(49, 82, 245, 0.14);
  transform: translateY(50%) rotate(45deg);
}

.chat-launcher:hover .chat-tooltip,
.chat-launcher:focus-visible .chat-tooltip {
  opacity: 1;
  transform: translateY(50%) translateX(0) scale(1);
}

.chat-launcher:hover {
  transform: translateY(-4px) scale(1.08);
  box-shadow: 0 22px 42px rgba(49, 82, 245, 0.24),
    0 0 0 8px rgba(228, 255, 38, 0.22);
}

.chat-window {
  position: absolute;
  right: 0;
  bottom: 86px;
  width: min(430px, calc(100vw - 2rem));
  height: min(680px, calc(100vh - 190px));
  border: 1px solid var(--assistant-border);
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 28px 70px rgba(15, 23, 42, 0.2);
  backdrop-filter: blur(14px);
}

.chat-header {
  padding: 1rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, var(--assistant-yellow), #f4ff97);
}

.header-brand {
  min-width: 0;
  display: flex;
  gap: 0.8rem;
  align-items: center;
}

.euroc-logo {
  width: auto;
  height: 44px;
  object-fit: contain;
}

.header-title strong,
.header-title span {
  display: block;
}

.header-title strong {
  color: #000;
  font-size: 1rem;
  font-weight: 900;
}

.header-title span {
  color: rgba(0, 0, 0, 0.62);
  font-size: 0.78rem;
  font-weight: 700;
}

.close-btn {
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.62);
  background: rgba(255, 255, 255, 0.45);
}

.messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: radial-gradient(
      circle at top right,
      rgba(49, 82, 245, 0.05),
      transparent 30%
    ),
    radial-gradient(
      circle at bottom left,
      rgba(228, 255, 38, 0.12),
      transparent 30%
    ),
    #fff;
}

.message {
  max-width: 88%;
  border-radius: 18px;
  padding: 0.85rem 1rem;
  margin: 0 0 0.85rem;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.55;
  white-space: pre-wrap;
}

.message.bot {
  margin-right: auto;
  border-top-left-radius: 6px;
  color: #0f172a;
  background: rgba(49, 82, 245, 0.08);
}

.message.user {
  margin-left: auto;
  border-top-right-radius: 6px;
  color: #fff;
  background: var(--assistant-blue);
}

.typing {
  margin-bottom: 0.85rem;
  display: flex;
  gap: 0.75rem;
  align-items: center;
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 800;
}

.typing-loader {
  display: flex;
  gap: 0.32rem;
  align-items: center;
}

.typing-loader span {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: var(--assistant-blue);
  animation: assistant-bounce 1.2s infinite ease-in-out;
}

.typing-loader span:nth-child(2) {
  animation-delay: 0.15s;
}

.typing-loader span:nth-child(3) {
  animation-delay: 0.3s;
}

.quick-actions {
  padding: 0.85rem 1rem 0.5rem;
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  background: #fff;
}

.quick-actions button {
  border: 0;
  border-radius: 999px;
  padding: 0.5rem 0.8rem;
  color: var(--assistant-blue);
  font-size: 0.78rem;
  font-weight: 800;
  background: rgba(49, 82, 245, 0.08);
}

.quick-actions button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.input-area {
  padding: 0.85rem 1rem 1rem;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  display: flex;
  gap: 0.6rem;
  background: #fff;
}

.input-area input {
  min-width: 0;
  flex: 1;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  padding: 0.7rem 0.95rem;
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 600;
  outline: none;
  background: #f8fafc;
}

.input-area input:focus {
  border-color: var(--assistant-blue);
  box-shadow: 0 0 0 0.18rem rgba(49, 82, 245, 0.14);
}

.input-area button {
  border: 0;
  border-radius: 999px;
  padding: 0 0.95rem;
  display: inline-flex;
  gap: 0.45rem;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 900;
  background: var(--assistant-blue);
}

.input-area button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.assistant-panel-enter-active,
.assistant-panel-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.assistant-panel-enter-from,
.assistant-panel-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

@keyframes assistant-bounce {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.5;
  }

  40% {
    transform: scale(1);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .chat-assistant {
    transition: none;
  }
}

@media (max-width: 576px) {
  .chat-tooltip {
    right: 0;
    bottom: calc(100% + 0.75rem);
    max-width: min(260px, calc(100vw - 2rem));
    transform: translateY(8px) scale(0.98);
  }

  .chat-tooltip::after {
    right: 1.25rem;
    bottom: -7px;
    border-top: 0;
    border-right: 1px solid rgba(49, 82, 245, 0.14);
    border-bottom: 1px solid rgba(49, 82, 245, 0.14);
  }

  .chat-launcher:hover .chat-tooltip,
  .chat-launcher:focus-visible .chat-tooltip {
    transform: translateY(0) scale(1);
  }

  .chat-launcher:hover {
    transform: translateY(-3px) scale(1.08);
  }

  .chat-launcher {
    width: 60px;
    height: 60px;
  }

  .chat-launcher-icon {
    font-size: 1.75rem;
  }

  .chat-assistant {
    right: 16px;
    bottom: 142px;
  }

  .chat-window {
    width: calc(100vw - 1.7rem);
    height: min(620px, calc(100vh - 180px));
  }

  .input-area button {
    width: 44px;
    padding: 0;
  }

  .input-area button i {
    margin: 0;
  }

  .input-area button {
    font-size: 0;
  }

  .input-area button i {
    font-size: 0.9rem;
  }
}
</style>
